"use client";

import { motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";

import { crossfadeVariants } from "@/lib/animations";
import { cn } from "@/lib/utils";

const BREAKPOINTS = [
  { min: 960, cols: 5 },
  { min: 720, cols: 4 },
  { min: 480, cols: 3 },
] as const;
const DEFAULT_COLS = 2;

const ASPECT_CLASSES = [
  "aspect-[4/5]",
  "aspect-square",
  "aspect-[3/4]",
  "aspect-[5/6]",
  "aspect-[4/5]",
  "aspect-square",
  "aspect-[3/4]",
  "aspect-[4/5]",
  "aspect-[5/6]",
  "aspect-square",
  "aspect-[4/5]",
  "aspect-[3/4]",
  "aspect-square",
  "aspect-[5/6]",
  "aspect-[4/5]",
  "aspect-[3/4]",
];

const ROWS_BY_COLS: Record<number, number> = { 2: 4, 3: 4, 4: 5, 5: 5 };

interface RecipeGridSkeletonProps {
  cols?: number;
  rows?: number;
  className?: string;
}

export function RecipeGridSkeleton({
  cols: fixedCols,
  rows: fixedRows,
  className,
}: RecipeGridSkeletonProps) {
  const gridRef = useRef<HTMLDivElement>(null);
  const [responsiveCols, setResponsiveCols] = useState(DEFAULT_COLS);

  const cols = fixedCols ?? responsiveCols;

  useEffect(() => {
    if (fixedCols != null) return;

    const el = gridRef.current;
    if (!el) return;

    const update = () => {
      const w = el.clientWidth;
      let c = DEFAULT_COLS;
      for (const bp of BREAKPOINTS) {
        if (w >= bp.min) {
          c = bp.cols;
          break;
        }
      }
      setResponsiveCols(c);
    };

    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [fixedCols]);

  const rows = fixedRows ?? ROWS_BY_COLS[cols] ?? 4;
  const total = cols * rows;
  const columns: number[][] = Array.from({ length: cols }, () => []);
  for (let i = 0; i < total; i++) {
    columns[i % cols].push(i);
  }

  return (
    <motion.div
      ref={gridRef}
      initial="hidden"
      animate="visible"
      exit="exit"
      variants={crossfadeVariants}
      className={cn("grid gap-2.5", className)}
      style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}
    >
      {columns.map((col, colIdx) => (
        <div key={colIdx} className="flex flex-col gap-[inherit]">
          {col.map((i) => (
            <div
              key={i}
              className={cn(
                "animate-pulse rounded-xl bg-muted",
                ASPECT_CLASSES[i % ASPECT_CLASSES.length]
              )}
            />
          ))}
        </div>
      ))}
    </motion.div>
  );
}
